import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { Nav } from "react-bootstrap";

import ChevronListItem from "@/modules/shared/ui/ChevronListItem";

import * as BusinessLogic from "@/lib/businessLogic";
import { classNames, niceNumber } from "@/lib/util";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as man_t from "@/lib/backend/manufacturing2.generated.types";
import { PEOPLE_TAGS } from "@/lib/ManufacturingTags.generated";

export type PersonStats = {
    hours_assigned: number;
    hours_max: number;
};

/**
 * Search method within the people list.
 */
export const includes = (needle: string, person: t.IPersonData): boolean => {
    needle = needle.toLowerCase();
    if (needle.length === 0) return true;
    if (person.name.toLowerCase().indexOf(needle) > -1) return true;
    if (person.external_id.indexOf(needle) > -1) return true;
    return false;
};

type Props = {
    filter: string;
    selected: t.IPersonData | null;
    people: t.IPersonData[];
    people_stats?: Map<string, PersonStats>;
    handlePeopleClick: (person: t.IPersonData) => void;
    narrow: boolean;
    activeFilter: boolean;
    plantFilter: string;
    line_group_uuid: string;
    showInactive: boolean;
};

class PeopleList extends Component<Props> {
    componentDidMount() {
        this.selectFirstPersonIfNeeded();
    }

    componentDidUpdate() {
        this.selectFirstPersonIfNeeded();
    }

    handlePeopleClick = (person: t.IPersonData) => {
        // Ignore clicks for currently selected person.
        if (person === this.props.selected) {
            return;
        }

        // Handle click in parent
        this.props.handlePeopleClick(person);
    };

    isPersonVisible = (person: t.IPersonData, line_group: man_t.ILineGroupData | null): boolean => {
        // Line group filter is disabled if user is using text search filter
        if (this.props.filter === "" && line_group && person.ted_id !== line_group.external_id) {
            return false;
        }

        if (!includes(this.props.filter, person)) return false;
        if (this.props.activeFilter && !this.props.showInactive && !person.active) return false;
        if (this.props.plantFilter !== "all" && !person.plant_uuids.includes(this.props.plantFilter)) return false;

        return true;
    };

    selectFirstPersonIfNeeded = () => {
        if (this.props.selected !== null) return;

        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        const first_visible = this.props.people.find(person => this.isPersonVisible(person, line_group));
        if (first_visible) {
            this.handlePeopleClick(first_visible);
        }
    };

    render() {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);

        // Generate people list and highlight the selected person
        const peopleList = this.props.people.map(person => {
            if (!this.isPersonVisible(person, line_group)) return null;

            const is_active = this.props.selected === person;

            const shiftBadges = person.shift_preference.map((val, i) => {
                const shift = i + 1;
                return val ? (
                    <span key={i} className={"badge badge-shift badge-shift-" + shift}>
                        {shift}
                    </span>
                ) : null;
            });

            const { uuid, name, external_id, adjuster, active } = person;
            const personWorks12h = person.tags[PEOPLE_TAGS.shift_board_works_12h_shifts] === "true";
            const person_stats = this.props.people_stats?.get(uuid);

            return (
                <ChevronListItem
                    key={uuid}
                    id={`person-list-person-${uuid}`}
                    text={
                        <>
                            <div>
                                <div>
                                    <span className={personWorks12h ? "person-12h-shift" : ""}>{name}</span>&nbsp;
                                    <span className="badge-list">
                                        {adjuster ? (
                                            <span className="badge badge-primary">
                                                <FormattedMessage id="common.adjuster" defaultMessage="Adjuster" />
                                            </span>
                                        ) : null}
                                        {!active ? (
                                            <span className="badge badge-secondary">
                                                <FormattedMessage id="common.inactive" defaultMessage="inactive" />
                                            </span>
                                        ) : null}
                                    </span>
                                </div>
                                <div className="pull-left">
                                    <span className="light">{external_id}</span>
                                </div>
                            </div>
                            <div className="d-flex flex-column align-items-end">
                                {person_stats && (
                                    <div
                                        className={classNames("person-stats badge badge-pill", {
                                            "badge-success":
                                                person_stats.hours_assigned === person_stats.hours_max &&
                                                person_stats.hours_assigned > 0,
                                            "badge-warning":
                                                person_stats.hours_assigned < person_stats.hours_max ||
                                                person_stats.hours_assigned === 0,
                                            "badge-danger": person_stats.hours_assigned > person_stats.hours_max,
                                        })}
                                    >
                                        <span>{niceNumber(person_stats.hours_assigned, 1)}</span>
                                        &nbsp;/&nbsp;
                                        <span>{niceNumber(person_stats.hours_max, 1)}</span> h
                                    </div>
                                )}
                                <div className="pull-right badge-list">{shiftBadges}</div>
                            </div>
                        </>
                    }
                    onClick={() => this.handlePeopleClick(person)}
                    is_active={is_active}
                />
            );
        });

        return (
            <Nav variant="tabs" className={this.props.narrow ? "nav-narrow" : ""}>
                <div className="nav-tabs-parent">{peopleList}</div>
            </Nav>
        );
    }
}

export default PeopleList;
